import axios from 'axios';
import { Message, MessageBox } from 'element-ui';
import store from '@/store';
import router from '@/router';
import { API_BASE_URL } from '@/config/api';

// 输出环境变量，用于调试
console.log('API基础URL:', API_BASE_URL);

// API请求超时时间（毫秒）
export const REQUEST_TIMEOUT = 15000; // 15秒

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: REQUEST_TIMEOUT // 请求超时时间，超过这个时间API会返回超时错误
});

console.log('Axios实例已创建，基础URL:', service.defaults.baseURL);

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 打印请求信息
    console.log('发送请求 =>', {
      url: config.url,
      method: config.method,
      params: config.params,
      data: config.data
    });
    
    const token = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType');
    
    if (token) {
      // 根据tokenType设置Authorization头，默认为bearer
      config.headers['Authorization'] = `${tokenType || 'Bearer'} ${token}`;
    }
    return config;
  },
  error => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 打印响应信息
    console.log('收到响应 <=', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    
    const res = response.data;
    
    // 检查响应是否包含code字段，这是API通用格式
    if (res.code !== undefined) {
      // 如果响应状态码不是200，则判断为错误
      if (res.code !== 200) {
        // 不在这里显示错误消息，让业务代码处理
        console.error('API返回错误:', res.code, res.msg, res);
        
        // 401: Token过期或未登录，需要特殊处理（登出）
        if (res.code === 401) {
          // 获取当前学校代码
          const schoolCode = router.currentRoute.params.schoolCode || localStorage.getItem('schoolCode');
          
          // 重新登录
          MessageBox.confirm(
            '您的登录已过期请重新登录',
            '确认登出',
            {
              confirmButtonText: '重新登录',
              cancelButtonText: '取消',
              type: 'warning'
            }
          ).then(() => {
            store.dispatch('auth/logout').then(() => {
              if (schoolCode && schoolCode !== 'undefined') {
                router.push(`/${schoolCode}/login?redirect=${router.currentRoute.fullPath}`);
              } else {
                router.push(`/login?redirect=${router.currentRoute.fullPath}`);
              }
            });
          });
        }
        
        // 返回一个带有code和msg的错误对象
        const error = new Error(res.msg || '系统错误');
        error.code = res.code;
        error.data = res.data;
        return Promise.reject(error);
      } else {
        // 返回响应的数据部分
        console.log('API返回成功, 数据部分:', res.data);
        return res.data;
      }
    } else {
      // 对于不符合通用格式的旧响应，保持向后兼容
      console.warn('响应不符合通用格式', res);
      return res;
    }
  },
  error => {
    // 打印错误信息
    console.error('响应拦截器错误:', {
      url: error.config?.url,
      message: error.message,
      response: error.response?.data
    });
    
    // 输出请求配置和响应详情，帮助调试
    if (error.config) {
      console.error('失败的请求配置:', {
        url: error.config.url,
        method: error.config.method,
        headers: error.config.headers,
        params: error.config.params,
        data: error.config.data
      });
    }
    
    // 处理HTTP状态码错误
    if (error.response) {
      console.error('错误状态码:', error.response.status);
      console.error('错误响应数据:', error.response.data);
      console.error('错误响应头:', error.response.headers);
      
      // 提取API返回的错误消息
      if (error.response.data && error.response.data.msg) {
        error.message = error.response.data.msg;
        console.log('使用API返回的错误消息:', error.message);
      }
      
      // 特殊处理：401状态码 - 登录过期
      if (error.response.status === 401) {
        Message.error('您的登录已过期请重新登录');
        
        // 获取当前学校代码
        const schoolCode = router.currentRoute.params.schoolCode || localStorage.getItem('schoolCode');
        
        // 如果当前在登录页面，则保持在当前页面
        if (router.currentRoute.name === 'Login') {
          return Promise.reject(error);
        }
        
        // 清除登录状态
        store.dispatch('auth/logout').then(() => {
          // 重定向到当前学校的登录页面
          if (schoolCode && schoolCode !== 'undefined') {
            router.push(`/${schoolCode}/login?redirect=${router.currentRoute.fullPath}`);
          } else {
            router.push(`/login?redirect=${router.currentRoute.fullPath}`);
          }
        });
      }
      // 特殊处理：仅对403未授权情况进行页面跳转
      else if (error.response.status === 403) {
        // 获取当前学校代码
        const schoolCode = router.currentRoute.params.schoolCode || localStorage.getItem('schoolCode');
        
        // 如果当前在登录页面，则保持在当前页面
        if (router.currentRoute.name === 'Login') {
          return Promise.reject(error);
        }
        
        // 否则重定向到当前学校的登录页面
        if (schoolCode && schoolCode !== 'undefined') {
          router.push(`/${schoolCode}/login`);
        } else {
          router.push('/login');
        }
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      console.error('没有收到响应:', error.request);
    } else {
      // 请求超时或网络错误 - 这种情况仍可保留一个消息，因为组件可能无法捕获这类错误
      console.error('网络错误或请求超时:', error.message);
    }
    return Promise.reject(error);
  }
);

export default service; 